// Day/night cycle, weather rolls, sky lighting, and rain particles.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// This subsystem had no assigned step in the migration plan; it's split
// out here (rather than dumped into main.ts) to keep the same one-module-
// per-concern structure as the rest of the migration. It does not know
// about `started`/`seasonOver` (main.ts's flags) or rest/sleep (economy/
// rest.ts) — those cross into updateEnvironment via plain parameters, and
// the result reports back what the caller needs to act on (a completed
// day, how much rest to drain) instead of reaching into those systems.

import * as THREE from 'three';
import { hash2, getTerrainSeed, HEX_H, SEA_LEVEL } from '@world/terrain';

export const SKY_COLOR = 0x7ec8ff;

export const DAY_LEN = 360; // seconds per in-game day

// How high the sun has to climb before it counts as daylight. Raising it
// pushes dawn later and dusk earlier, so night takes up more of the cycle:
// at 0.40 the light runs from about frac 0.32 to 0.68, roughly a third day
// to two thirds night. Night is meant to be fished now there's a lantern.
const DAY_THRESHOLD = 0.40;
const DAY_GAIN = 1 / (1 - DAY_THRESHOLD);
// Exact share of the cycle that is lit, given the threshold above.
const DAY_FRAC = (Math.PI - 2 * Math.asin(DAY_THRESHOLD)) / (2 * Math.PI);
const NIGHT_FRAC = 1 - DAY_FRAC;
// Rest lost over one whole night, spread across however long the night
// actually runs — expressed this way so lengthening the day or the night
// doesn't quietly make sleep deprivation several times harsher.
const REST_LOST_PER_NIGHT = 69;
const REST_DRAIN_RATE = REST_LOST_PER_NIGHT / (DAY_LEN * NIGHT_FRAC);

// Begin in the morning. net/rooms.ts mirrors this value for multiplayer
// clock sync — change both together or joiners start at a different hour.
let gameTime = DAY_LEN * 0.4;
let lastEnvDay = -1;

export interface Env {
  day: number;
  frac: number;
  weather: string;
  fullMoon: boolean;
  daylight: number;
  night: boolean;
  fastNight: boolean;
}

export const ENV: Env = { day: 0, frac: 0.3, weather: 'Clear', fullMoon: false, daylight: 1, night: false, fastNight: false };

let weatherSeed = 0;

// Call once, right after world/terrain.ts's applySeed(n) — weather is
// seeded from the terrain seed, same as legacy's applySeed side effect.
export function applyWeatherSeed(): void {
  const { q } = getTerrainSeed();
  weatherSeed = Math.floor(q * 917) % 100000;
}

// TESTING ONLY — set back to null so weather is seeded again.
const FORCE_WEATHER: string | null = 'Storm';

export function weatherForDay(day: number): string {
  if (FORCE_WEATHER) return FORCE_WEATHER;
  const h = hash2(day * 7919 + 31, weatherSeed);
  if (h < 0.42) return 'Clear';
  if (h < 0.68) return 'Cloudy';
  if (h < 0.88) return 'Rain';
  return 'Storm';
}
export function moonPhase(day: number): number {
  return ((day % 8) + 8) % 8;
}
export function isFullMoon(day: number): boolean {
  return moonPhase(day) === 4;
}

export function gameTimeNow(): number {
  return gameTime;
}

// Used by the sleep mechanic (economy/rest.ts) to jump time forward.
export function addGameTime(delta: number): void {
  gameTime += delta;
}
export function setGameTime(t: number): void {
  gameTime = t;
}

// Multiplayer only: raises gameTime to meet the server's authoritative
// floor, never lowers it — preserves local fast-night/sleep progress,
// only catches up a client that fell behind (e.g. a backgrounded tab).
export function syncGameTimeFloor(t: number): void {
  if (t > gameTime) gameTime = t;
}

// ================= Lights =================
// Ambient-dominant on purpose: the watercolour reference gets its
// luminosity from the paper, not from a sun, so form shading is kept
// deliberately weak. The directional lights survive only to give a hint of
// direction — see the intensity curve in updateWeather below.
export const ambient = new THREE.AmbientLight(0xffffff, 1.0);
export const sun = new THREE.DirectionalLight(0xfff4d6, 0.38);
sun.position.set(30, 50, 20);
export const fill = new THREE.DirectionalLight(0xaad4ff, 0.14);
fill.position.set(-20, 20, -30);

// ================= Rain particles (shared by rain and storms) =================
const RAIN_N = 480;
const rainPos = new Float32Array(RAIN_N * 3);
for (let i = 0; i < RAIN_N; i++) {
  rainPos[i * 3] = (Math.random() - 0.5) * 36;
  rainPos[i * 3 + 1] = Math.random() * 14;
  rainPos[i * 3 + 2] = (Math.random() - 0.5) * 36;
}
const rainGeo = new THREE.BufferGeometry();
rainGeo.setAttribute('position', new THREE.BufferAttribute(rainPos, 3));
const rainMat = new THREE.PointsMaterial({ color: 0xbcd6ee, size: 0.07, transparent: true, opacity: 0.55 });
export const rainPts = new THREE.Points(rainGeo, rainMat);
rainPts.visible = false;
rainPts.frustumCulled = false;

let lightningFlash = 0, lightningTimer = 5;

const cNight = new THREE.Color(0x0d1a38), cDay = new THREE.Color(0x7ec8ff),
      cDusk = new THREE.Color(0xef9a66), cGrey = new THREE.Color(0x93a2b2),
      cStorm = new THREE.Color(0x424d59), cWhite = new THREE.Color(0xffffff);
const skyColor = new THREE.Color();

// ================= Mist band =================
// Horizontal veils lying low over the water. Each is a quad that follows the
// player, carrying a radial alpha ramp — clear overhead, thickening outward
// — so the distance sinks into haze while roofs, masts and jetty posts stay
// above the layer and read as landmarks standing out of it. The distance fog
// handles depth; this adds the flat banding that reads as ink-wash mist.
//
// Three unlit quads with no depth writes, so it costs three draw calls and
// nothing else. Matches water.ts's WATER_SURF_Y formula rather than
// importing it for one constant, as chunks.ts does.
const MIST_WATER_Y = SEA_LEVEL * HEX_H + HEX_H * 0.2;
const MIST_LAYERS = [
  { y: 0.05, opacity: 0.42 },
  { y: 0.13, opacity: 0.34 },
  { y: 0.22, opacity: 0.26 },
];
// Half-width. Deliberately past the fog's far plane, so the quad's own rim —
// where the ramp reaches full strength — sits in already-opaque haze and is
// never visible as an edge.
const MIST_REACH = 20;

// ================= Visibility =================
// Daytime view distance, set once from the terrain's load radius (passed in
// rather than imported, because chunks.ts already reaches back into this
// module via trees.ts and importing it here would close a cycle).
let fogDayFar = 13;
export function configureFog(dayFar: number): void {
  fogDayFar = dayFar;
}
// Night shuts the world down to roughly the hex underfoot. These are
// fractions of the daytime distance, and the floor matters: fog is measured
// from the *camera*, which orbits a couple of units out, so anything much
// below ~0.25 starts fogging out the fisherman himself.
const NIGHT_REACH = 0.26;
const NIGHT_REACH_FULL_MOON = 0.45;
// How much of the remaining gap a lit lantern buys back.
const LANTERN_REACH = 0.55;
// Fog band as a fraction of the far plane. Tighter than a typical outdoor
// fog so that at night the near plane still clears the camera distance.
const FOG_NEAR_FRAC = 0.52;

function makeMistTexture(): THREE.CanvasTexture {
  const S = 256;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(S / 2, S / 2, S * 0.12, S / 2, S / 2, S * 0.5);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0.95)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export const mistGroup = new THREE.Group();
const mistMats: THREE.MeshBasicMaterial[] = [];
{
  const tex = makeMistTexture();
  const geo = new THREE.PlaneGeometry(MIST_REACH * 2, MIST_REACH * 2);
  geo.rotateX(-Math.PI / 2);
  MIST_LAYERS.forEach((layer) => {
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, depthWrite: false, fog: false,
      opacity: layer.opacity, side: THREE.DoubleSide,
    });
    const m = new THREE.Mesh(geo, mat);
    m.position.y = MIST_WATER_Y + layer.y;
    m.renderOrder = 3; // after the water surface, which is also transparent
    mistMats.push(mat);
    mistGroup.add(m);
  });
}

export interface EnvironmentUpdateOptions {
  running: boolean; // started && !seasonOver
  sleeping: boolean; // sleepAnim > 0, from economy/rest.ts
  playerX: number;
  playerZ: number;
  scene: THREE.Scene;
  // 0..1 — how strongly a lit lantern is pushing the darkness back.
  lanternLight?: number;
}

export interface EnvironmentUpdateResult {
  // The day index that just completed, if a day boundary was crossed this tick.
  dayChanged: number | null;
  // Rest to subtract this frame (being awake at night wears him down); 0 otherwise.
  restDrain: number;
}

export function updateEnvironment(dt: number, opts: EnvironmentUpdateOptions): EnvironmentUpdateResult {
  // Night runs at normal pace when there's something to see by — a full
  // moon, or a lit lantern. Without that exemption the lantern would be
  // pointless: the night it exists to make playable would be skipped past
  // at six times speed.
  ENV.fastNight = ENV.night && !ENV.fullMoon && !(opts.lanternLight ?? 0);
  let restDrain = 0;
  if (opts.running) {
    gameTime += dt * (ENV.fastNight ? 6 : 1);
    if (ENV.night && !opts.sleeping) {
      restDrain = dt * (ENV.fastNight ? 6 : 1) * REST_DRAIN_RATE;
    }
  }
  const day = Math.floor(gameTime / DAY_LEN);
  const frac = (gameTime % DAY_LEN) / DAY_LEN;
  const sunH = Math.sin((frac - 0.25) * Math.PI * 2); // -1 midnight, +1 noon
  const daylight = Math.max(0, Math.min(1, (sunH - DAY_THRESHOLD) * DAY_GAIN));
  // Dusk glow peaks where the light actually crosses over, which is now the
  // threshold rather than the horizon.
  const dusk = Math.max(0, 1 - Math.abs(sunH - DAY_THRESHOLD) * 3);
  const weather = weatherForDay(day);
  const full = isFullMoon(day);

  ENV.day = day; ENV.frac = frac; ENV.weather = weather;
  ENV.fullMoon = full; ENV.daylight = daylight; ENV.night = daylight < 0.12;

  let dayChanged: number | null = null;
  if (opts.running && lastEnvDay >= 0 && day !== lastEnvDay) dayChanged = lastEnvDay;
  lastEnvDay = day;

  const wDim = weather === 'Storm' ? 0.45 : weather === 'Rain' ? 0.62 : weather === 'Cloudy' ? 0.85 : 1;

  skyColor.copy(cNight).lerp(cDay, daylight);
  if (weather === 'Cloudy') skyColor.lerp(cGrey, 0.35 * (0.3 + daylight * 0.7));
  else if (weather === 'Rain') skyColor.lerp(cGrey, 0.6 * (0.3 + daylight * 0.7));
  else if (weather === 'Storm') skyColor.lerp(cStorm, 0.75);
  skyColor.lerp(cDusk, dusk * 0.55 * wDim);

  if (weather === 'Storm') {
    lightningTimer -= dt;
    if (lightningTimer <= 0) {
      lightningFlash = 1;
      lightningTimer = 4 + Math.random() * 8;
    }
  }
  if (lightningFlash > 0) {
    skyColor.lerp(cWhite, Math.min(1, lightningFlash) * 0.75);
    lightningFlash -= dt * 5;
  }

  (opts.scene.background as THREE.Color).copy(skyColor);
  const fog = opts.scene.fog as THREE.Fog;
  fog.color.copy(skyColor);

  // View distance collapses after dark — at new moon with no lantern it
  // closes to about the hex he's standing on. A full moon, or a lit
  // lantern, buys some of that back.
  const lit = Math.max(0, Math.min(1, opts.lanternLight ?? 0));
  const darkReach = full ? NIGHT_REACH_FULL_MOON : NIGHT_REACH;
  const nightReach = darkReach + (1 - darkReach) * lit * LANTERN_REACH;
  fog.far = fogDayFar * (nightReach + (1 - nightReach) * daylight);
  fog.near = fog.far * FOG_NEAR_FRAC;

  // Mist rides with the player and takes the sky's colour, so it always
  // reads as the same air the fog is made of, and thickens in bad weather.
  mistGroup.position.set(opts.playerX, 0, opts.playerZ);
  const mistBoost = weather === 'Storm' ? 1.55 : weather === 'Rain' ? 1.3 : weather === 'Cloudy' ? 1.15 : 1;
  mistMats.forEach((m, i) => {
    m.color.copy(skyColor);
    m.opacity = MIST_LAYERS[i].opacity * mistBoost;
  });

  const moonBoost = full ? 0.22 : 0.03;
  sun.intensity = 0.06 + 0.32 * daylight * wDim + Math.max(0, lightningFlash) * 0.8;
  ambient.intensity = (full ? 0.36 : 0.22) + 0.78 * daylight * (0.5 + wDim * 0.5);
  fill.intensity = 0.14 * daylight + moonBoost * (1 - daylight);

  const raining = weather === 'Rain' || weather === 'Storm';
  rainPts.visible = raining;
  if (raining) {
    const speed = weather === 'Storm' ? 20 : 12;
    const windX = weather === 'Storm' ? 4 : 1;
    const arr = rainGeo.attributes.position.array as Float32Array;
    for (let i = 0; i < RAIN_N; i++) {
      arr[i * 3 + 1] -= speed * dt;
      arr[i * 3] += windX * dt;
      if (arr[i * 3 + 1] < 0) {
        arr[i * 3] = opts.playerX + (Math.random() - 0.5) * 36;
        arr[i * 3 + 1] = 12 + Math.random() * 3;
        arr[i * 3 + 2] = opts.playerZ + (Math.random() - 0.5) * 36;
      }
    }
    rainGeo.attributes.position.needsUpdate = true;
    rainMat.opacity = weather === 'Storm' ? 0.7 : 0.5;
  }

  return { dayChanged, restDrain };
}
